import { RunningWithErrorsOutlined as RunningWithErrorsIcon } from "@mui/icons-material";
import {
  Box,
  GridLegacy as Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TableSortLabel,
} from "@mui/material";
import { visuallyHidden } from "@mui/utils";
import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { AdminEmptyState } from "@/components/common/AdminEmptyState";
import { LongRunningDataLoader } from "@/components/common/LongRunningDataLoader";
import { TableSkeletonRows } from "@/components/common/TableSkeletonRows";
import { tableInCardSx } from "@/components/common/unifiedTableSx";
import { getReportNetworkLoading } from "@/hooks/getReportNetworkLoading";
import { useMissedOccurrences } from "@/hooks/useMissedOccurrences";
import { useMissedRequests } from "@/hooks/useMissedRequests";
import MissedOccurrencesTable from "@/pages/reports/missed/MissedOccurrencesTable";
import { downloadCsv } from "@/pages/reports/shared/storeInsightsCsv";
import { StoreInsightsPanel } from "@/pages/reports/shared/StoreInsightsPanel";
import { StoreInsightsReportHeader } from "@/pages/reports/shared/StoreInsightsReportHeader";
import { StoreInsightsSummaryCardsRow } from "@/pages/reports/shared/StoreInsightsSummaryCardsRow";
import { useTrendCardPresentation } from "@/pages/reports/shared/useTrendCardPresentation";
import { useStoreInsightsReportFilters } from "@/pages/reports/shared/useStoreInsightsReportFilters";
import { useStoreInsightsTrendComparison } from "@/pages/store-insights/shared/useStoreInsightsTrendComparison";
import { storePerformanceService } from "@/services/StorePerformanceService";
import {
  CurrencyCode,
  formatCurrencyWithNoDecimals,
  getStoreCurrencyIdentifiers,
} from "@/utils/currency";
import { formatInteger, formatPercentFromWhole } from "@/utils/number";
import {
  formatStoreDateRange,
  formatStoreDateTimeFileStamp,
  getBrowserTimezone,
  getInclusiveStoreDayCount,
  getStoreDayIndex,
} from "@/utils/storeTime";

interface DaySummaryRow {
  day: string;
  dayIndex: number;
  missedHelp: number;
  missedItems: number;
  totalValue: number;
}

type DaySummaryOrderKey = keyof Omit<DaySummaryRow, "dayIndex">;

const MissedReport: React.FC = () => {
  const { t, i18n } = useTranslation();
  const browserTimezone = useMemo(() => getBrowserTimezone(), []);
  const {
    user,
    isCraveUser,
    selectedStores,
    loading,
    selectionLoading,
    period,
    customRange,
    currentDateRange,
    deviceType,
    customers,
    selectedCustomerIds,
    groups,
    selectedGroups,
    groupCounts,
    filteredStores,
    onCustomerChange,
    onGroupsChange,
    onStoresChange,
    onPeriodChange,
    onCustomRangeChange,
  } = useStoreInsightsReportFilters({ enableDeviceFilter: false });
  const {
    comparisonWindow,
    setComparisonWindow,
    anchorComparisonToWeekdays,
    setAnchorComparisonToWeekdays,
  } = useStoreInsightsTrendComparison(period, currentDateRange);
  const comparisonRange = useMemo(
    () =>
      storePerformanceService.getTrendComparisonRange(
        currentDateRange,
        comparisonWindow,
        browserTimezone,
        anchorComparisonToWeekdays,
      ),
    [anchorComparisonToWeekdays, browserTimezone, comparisonWindow, currentDateRange],
  );

  const [dayOrder, setDayOrder] = useState<"asc" | "desc">("desc");
  const [dayOrderBy, setDayOrderBy] = useState<DaySummaryOrderKey>("missedHelp");
  const occurrencesRef = useRef<HTMLDivElement>(null);

  const missedRequestsData = useMissedRequests(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
    comparisonRange,
  );
  const missedRequestsCountLabel = missedRequestsData.pillText?.split(" ")[0] || "0";
  const missedRequestsValueLabel = (() => {
    const suffix = t("common.analytics.itemRequestsValue", { value: "" }).trim();
    if (missedRequestsData.subtitle.endsWith(suffix)) {
      return missedRequestsData.subtitle.slice(0, -suffix.length).trim() || "--";
    }
    return "--";
  })();
  const occurrences = useMissedOccurrences(
    selectedStores.length ? selectedStores : null,
    currentDateRange,
    deviceType,
  );
  const networkLoading = getReportNetworkLoading(
    loading,
    selectionLoading,
    missedRequestsData.loading,
    occurrences.loading,
  );
  const contentLoading = networkLoading;
  const displayedDayCount = useMemo(
    () => getInclusiveStoreDayCount(currentDateRange.start, currentDateRange.end, browserTimezone),
    [browserTimezone, currentDateRange.end, currentDateRange.start],
  );
  const loadingKey = useMemo(
    () =>
      [
        selectedStores.map((store) => store.id).join(","),
        currentDateRange.start.getTime(),
        currentDateRange.end.getTime(),
        deviceType,
      ].join("|"),
    [currentDateRange.end, currentDateRange.start, deviceType, selectedStores],
  );
  const trendDateRange = useMemo(() => {
    const range = formatStoreDateRange(
      comparisonRange.start,
      comparisonRange.end,
      browserTimezone,
      i18n.resolvedLanguage || i18n.language,
      { weekday: "short", month: "short", day: "numeric" },
    );
    return range.startDate === range.endDate
      ? range.startDate
      : `${range.startDate} – ${range.endDate}`;
  }, [
    browserTimezone,
    comparisonRange.end,
    comparisonRange.start,
    i18n.language,
    i18n.resolvedLanguage,
  ]);
  const trendProps = useTrendCardPresentation(trendDateRange);
  const storeTimezones = useMemo(
    () => Object.fromEntries(selectedStores.map((store) => [store.id, store.timezone])),
    [selectedStores],
  );

  const daySummary = useMemo<DaySummaryRow[]>(() => {
    const daysOfWeek = [
      t("storeInsights.common.days.sunday"),
      t("storeInsights.common.days.monday"),
      t("storeInsights.common.days.tuesday"),
      t("storeInsights.common.days.wednesday"),
      t("storeInsights.common.days.thursday"),
      t("storeInsights.common.days.friday"),
      t("storeInsights.common.days.saturday"),
    ];
    const summary = new Map<
      number,
      { missedHelp: number; missedItems: number; totalValue: number }
    >();

    occurrences.data.forEach((row) => {
      const dayIndex = getStoreDayIndex(row.createdAt, storeTimezones[row.storeId]);
      if (dayIndex === null) {
        return;
      }

      const current = summary.get(dayIndex) ?? { missedHelp: 0, missedItems: 0, totalValue: 0 };

      const type = row.type.toUpperCase();
      // Help and door requests are typically not related to items
      if (
        type === "ITEM_NOT_FOUND" ||
        type === "ITEM_REQUEST" ||
        type === "ITEM" ||
        type === "MOBILE_ITEM"
      ) {
        current.missedItems += 1;
      } else {
        current.missedHelp += 1;
      }

      current.totalValue += row.value ?? 0;
      summary.set(dayIndex, current);
    });

    return daysOfWeek
      .map((day, dayIndex) => {
        const stats = summary.get(dayIndex) ?? { missedHelp: 0, missedItems: 0, totalValue: 0 };
        return {
          day,
          dayIndex,
          missedHelp: stats.missedHelp,
          missedItems: stats.missedItems,
          totalValue: stats.totalValue,
        };
      })
      .filter((row) => row.missedHelp > 0 || row.missedItems > 0);
  }, [occurrences.data, storeTimezones, t]);

  const sortedDaySummary = useMemo(() => {
    return [...daySummary].sort((a, b) => {
      if (dayOrderBy === "day") {
        const aValue = a.dayIndex;
        const bValue = b.dayIndex;
        return dayOrder === "desc" ? bValue - aValue : aValue - bValue;
      }

      const aValue = a[dayOrderBy as keyof DaySummaryRow];
      const bValue = b[dayOrderBy as keyof DaySummaryRow];

      if (typeof aValue === "number" && typeof bValue === "number") {
        if (bValue < aValue) return dayOrder === "desc" ? -1 : 1;
        if (bValue > aValue) return dayOrder === "desc" ? 1 : -1;
        return 0;
      }
      return 0;
    });
  }, [daySummary, dayOrder, dayOrderBy]);

  const handleDaySort = (property: DaySummaryOrderKey) => {
    const isAsc = dayOrderBy === property && dayOrder === "asc";
    setDayOrder(isAsc ? "desc" : "asc");
    setDayOrderBy(property);
  };

  const [currencyCode, currencySymbol] = useMemo(
    () => getStoreCurrencyIdentifiers(selectedStores),
    [selectedStores],
  );
  const currentLocale = useMemo(
    () => new Intl.Locale(i18n.resolvedLanguage || i18n.language || "en-US"),
    [i18n.resolvedLanguage, i18n.language],
  );

  const formatRoundedAmount = (value: number) =>
    formatCurrencyWithNoDecimals(
      Math.round(value),
      currencyCode as CurrencyCode,
      currencySymbol,
      currentLocale,
    );

  const handleExportDaySummary = () => {
    if (daySummary.length === 0) return;

    downloadCsv(
      [
        [
          t("storeInsights.common.fields.day"),
          t("storeInsights.common.fields.missedHelp"),
          t("storeInsights.common.fields.missedItems"),
          t("storeInsights.common.fields.value"),
        ],
        ...sortedDaySummary.map((row) => [
          row.day,
          row.missedHelp,
          row.missedItems,
          formatCurrencyWithNoDecimals(
            row.totalValue,
            currencyCode as CurrencyCode,
            currencySymbol,
            currentLocale,
          ),
        ]),
      ],
      `day_export_${formatStoreDateTimeFileStamp(new Date(), browserTimezone)}.csv`,
    );
  };

  return (
    <Box sx={{ pb: 4 }}>
      <StoreInsightsReportHeader
        title={t("storeInsights.missedReport.title")}
        icon={<RunningWithErrorsIcon />}
        isCraveUser={isCraveUser}
        currentUserRole={user?.role}
        customers={customers}
        selectedCustomerIds={selectedCustomerIds}
        onCustomerChange={onCustomerChange}
        groups={groups}
        selectedGroups={selectedGroups}
        groupCounts={groupCounts}
        onGroupsChange={onGroupsChange}
        filteredStores={filteredStores}
        selectedStores={selectedStores}
        onStoresChange={onStoresChange}
        loading={loading}
        networkLoading={networkLoading}
        period={period}
        currentDateRange={currentDateRange}
        customRange={customRange}
        onPeriodChange={onPeriodChange}
        onCustomRangeChange={onCustomRangeChange}
        deviceTypeFilterEnabled
        deviceTypeFilterDisabled
        selectedDeviceType="all"
        showTrendSelector
        trendPeriod={comparisonWindow}
        onTrendPeriodChange={setComparisonWindow}
        trendComparisonRange={comparisonRange}
        anchorTrendComparisonToWeekdays={anchorComparisonToWeekdays}
        onAnchorTrendComparisonToWeekdaysChange={setAnchorComparisonToWeekdays}
      />

      <Box sx={{ p: 2.5, pt: 0, mt: 3 }}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StoreInsightsSummaryCardsRow
              title={t("storeInsights.common.sections.requestsBreakdown")}
              cards={[
                {
                  value: missedRequestsData.value,
                  previousText: missedRequestsData.previousValue,
                  title: t("storeInsights.missedReport.cards.missedRequests"),
                  isPositive: missedRequestsData.isPositive,
                  subtitle: missedRequestsData.subtitle,
                  subtitleLoading: contentLoading,
                  pillText: missedRequestsData.pillText,
                  pillTextAsChip: true,
                  trend: trendProps(
                    missedRequestsData.percentageTrend,
                    (value, locale) => formatPercentFromWhole(value, locale),
                    missedRequestsData.percentageComparisonProgress,
                  ),
                },
                {
                  value: missedRequestsValueLabel,
                  previousText: null,
                  title: t("storeInsights.missedReport.cards.valueOfMissedRequests"),
                  isPositive: missedRequestsData.isPositive,
                  subtitle: t("storeInsights.missedReport.cards.valueOfMissedRequestsSubtitle", {
                    countLabel: missedRequestsCountLabel,
                  }),
                  subtitleLoading: contentLoading,
                  pillText: missedRequestsData.value,
                  pillTextAsChip: true,
                  trend: trendProps(missedRequestsData.monetaryTrend, (value) =>
                    formatCurrencyWithNoDecimals(
                      value,
                      currencyCode as CurrencyCode,
                      currencySymbol,
                    ),
                  ),
                },
              ]}
            />
          </Grid>

          <Grid item xs={12}>
            <StoreInsightsPanel
              title={t("storeInsights.common.sections.dayOfWeek")}
              onExport={handleExportDaySummary}
              exportDisabled={contentLoading || daySummary.length === 0}
            >
              {contentLoading ? (
                <Box sx={{ mb: 2.5 }}>
                  <LongRunningDataLoader
                    variant="table"
                    isLoading={true}
                    baseLabels={[
                      t("storeInsights.common.loading.daySummary"),
                      t("storeInsights.common.loading.groupingResults"),
                      t("storeInsights.common.loading.calculatingInsights"),
                      t("storeInsights.common.loading.preparingResults"),
                    ]}
                    loopingLabels={[
                      t("storeInsights.common.loading.stillWorking"),
                      t("storeInsights.common.loading.thanksForPatience"),
                    ]}
                    estimate={{
                      meanTimePerStoreMs: 600,
                      storeCount: selectedStores.length,
                      displayedDayCount,
                      growth: "logarithmic",
                      logarithmicLinearWeight: 0.1,
                    }}
                    locale={i18n.resolvedLanguage || i18n.language}
                    loadingKey={loadingKey}
                    estimateLabel={(duration) =>
                      t("storeComparison.loading.usuallyUnder", { duration })
                    }
                  />
                </Box>
              ) : null}
              <Table sx={tableInCardSx}>
                <TableHead>
                  <TableRow
                    className="text-[#003366] border-b border-gray-100"
                    style={{ fontWeight: 600 }}
                  >
                    <TableCell className="text-left rtl:text-right" style={{ fontWeight: 500 }}>
                      <TableSortLabel
                        active={dayOrderBy === "day"}
                        direction={dayOrderBy === "day" ? dayOrder : "asc"}
                        onClick={() => handleDaySort("day")}
                      >
                        {t("storeInsights.common.fields.day")}
                        {dayOrderBy === "day" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {dayOrder === "desc"
                              ? t("storeInsights.common.sortDirection.descending")
                              : t("storeInsights.common.sortDirection.ascending")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" style={{ fontWeight: 500 }}>
                      <TableSortLabel
                        active={dayOrderBy === "missedHelp"}
                        direction={dayOrderBy === "missedHelp" ? dayOrder : "asc"}
                        onClick={() => handleDaySort("missedHelp")}
                      >
                        {t("storeInsights.common.fields.missedHelp")}
                        {dayOrderBy === "missedHelp" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {dayOrder === "desc"
                              ? t("storeInsights.common.sortDirection.descending")
                              : t("storeInsights.common.sortDirection.ascending")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" style={{ fontWeight: 500 }}>
                      <TableSortLabel
                        active={dayOrderBy === "missedItems"}
                        direction={dayOrderBy === "missedItems" ? dayOrder : "asc"}
                        onClick={() => handleDaySort("missedItems")}
                      >
                        {t("storeInsights.common.fields.missedItems")}
                        {dayOrderBy === "missedItems" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {dayOrder === "desc"
                              ? t("storeInsights.common.sortDirection.descending")
                              : t("storeInsights.common.sortDirection.ascending")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" style={{ fontWeight: 500 }}>
                      <TableSortLabel
                        active={dayOrderBy === "totalValue"}
                        direction={dayOrderBy === "totalValue" ? dayOrder : "asc"}
                        onClick={() => handleDaySort("totalValue")}
                      >
                        {t("storeInsights.common.fields.value")}
                        {dayOrderBy === "totalValue" ? (
                          <Box component="span" sx={visuallyHidden}>
                            {dayOrder === "desc"
                              ? t("storeInsights.common.sortDirection.descending")
                              : t("storeInsights.common.sortDirection.ascending")}
                          </Box>
                        ) : null}
                      </TableSortLabel>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {contentLoading ? (
                    <TableSkeletonRows columnCount={4} />
                  ) : daySummary.length > 0 ? (
                    sortedDaySummary.map((row) => (
                      <TableRow key={row.day}>
                        <TableCell
                          className="text-[#001F3F] text-left rtl:text-right"
                          style={{ fontWeight: 700 }}
                        >
                          {row.day}
                        </TableCell>
                        <TableCell
                          align="center"
                          className="text-[#001F3F]"
                          style={{ fontWeight: 500 }}
                        >
                          {formatInteger(row.missedHelp, currentLocale)}
                        </TableCell>
                        <TableCell
                          align="center"
                          className="text-[#001F3F]"
                          style={{ fontWeight: 500 }}
                        >
                          {formatInteger(row.missedItems, currentLocale)}
                        </TableCell>
                        <TableCell
                          align="center"
                          className="text-[#001F3F]"
                          style={{ fontWeight: 500 }}
                        >
                          {formatRoundedAmount(row.totalValue)}
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow className="table-empty-state">
                      <TableCell colSpan={4} align="center" className="py-8">
                        <AdminEmptyState
                          icon={<RunningWithErrorsIcon sx={{ fontSize: 40 }} />}
                          text={t("storeInsights.common.empty.noDayData")}
                        />
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </StoreInsightsPanel>
          </Grid>

          <Grid item xs={12} ref={occurrencesRef}>
            <MissedOccurrencesTable
              data={occurrences.data}
              loading={contentLoading}
              error={occurrences.error}
              storeTimezones={storeTimezones}
              onRequestImage={occurrences.loadImageForOccurrence}
              stores={selectedStores}
              loadingKey={loadingKey}
              displayedDayCount={displayedDayCount}
            />
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default MissedReport;
